import EventEmitter from 'eventemitter3'

import { WorldSystem } from './systems/WorldSystem'
import { PrefsSystem } from './systems/PrefsSystem'
import { AudioSystem } from './systems/AudioSystem'
import { InventorySystem } from './systems/InventorySystem'
import { MediaSystem } from './systems/MediaSystem'
import { PhysicsSystem } from './systems/PhysicsSystem'
import { GraphicsSystem } from './systems/GraphicsSystem'
import { NodesSystem } from './systems/NodesSystem'
import { InputSystem } from './systems/InputSystem'
import { ChunksSystem } from './systems/ChunksSystem'
import { EntitiesSystem } from './systems/EntitiesSystem'
import { SpatialSystem } from './systems/SpatialSystem'
import { LoaderSystem } from './systems/LoaderSystem'
import { SkySystem } from './systems/SkySystem'
import { NetworkSystem } from './systems/NetworkSystem'
import { AvatarsSystem } from './systems/AvatarsSystem'
import { StatsSystem } from './systems/Stats'

import * as THREE from './three'
import { locationPool } from '../core/Location'
import { Registry } from '../core/Registry'
import { Atlas } from '../core/Atlas'

window.THREE = THREE

export class Client extends EventEmitter {
  constructor() {
    super()
    window.client = this
    this.time = 0
    this.frame = 0
    this.delta = 0
    this.maxDeltaTime = 1 / 30 // 0.33333
    this.fixedDeltaTime = 1 / 50 // 0.01666
    this.accumulator = 0
    this.systems = []
    this.hot = new Set()

    this.registry = new Registry({ label: 'Client', atlas: new Atlas({ label: 'Client' }) })

    this.register('world', WorldSystem)
    this.register('prefs', PrefsSystem)
    this.register('stats', StatsSystem)
    this.register('audio', AudioSystem)
    this.register('inventory', InventorySystem)
    this.register('media', MediaSystem)
    this.register('physics', PhysicsSystem)
    this.register('graphics', GraphicsSystem)
    this.register('nodes', NodesSystem)
    this.register('input', InputSystem)
    this.register('chunks', ChunksSystem)
    this.register('entities', EntitiesSystem)
    this.register('spatial', SpatialSystem)
    this.register('loader', LoaderSystem)
    this.register('sky', SkySystem)
    this.register('network', NetworkSystem)
    this.register('avatars', AvatarsSystem)
  }

  register(key, System) {
    const system = new System(this)
    this.systems.push(system)
    this[key] = system
    return system
  }

  async init(options) {
    for (const system of this.systems) {
      await system.init(options)
    }
    this.start()
  }

  start() {
    for (const system of this.systems) {
      system.start()
    }
    this.emit('ready', true)
  }

  tick = time => {
    // update time, delta, frame and accumulator
    time /= 1000
    let delta = time - this.time
    if (delta < 0) delta = 0
    if (delta > this.maxDeltaTime) {
      delta = this.maxDeltaTime
    }
    this.time = time
    this.frame++
    this.delta = delta
    this.accumulator += delta
    // clear pools
    locationPool.clear()
    // begin any stats/performance monitors
    this.preTick(delta)
    // run pre fixed update
    const willFixedStep = this.accumulator >= this.fixedDeltaTime
    this.preFixedUpdate(willFixedStep)
    // run as many fixed updates as we can for this ticks delta
    while (this.accumulator >= this.fixedDeltaTime) {
      // run all fixed updates
      this.fixedUpdate(this.fixedDeltaTime)
      // step physics
      this.postFixedUpdate(this.fixedDeltaTime)
      // decrement accumulator
      this.accumulator -= this.fixedDeltaTime
    }
    // interpolate physics for remaining delta time
    const alpha = this.accumulator / this.fixedDeltaTime
    this.preUpdate(alpha)
    // run all updates
    this.update(delta)
    // run post updates
    this.postUpdate(delta)
    // run late updates
    this.lateUpdate(delta)
    // run post late updates
    this.postLateUpdate(delta)
    // commit
    this.commit(delta)
    // end any stats/performance monitors
    this.postTick()
  }

  preTick(delta) {
    for (const system of this.systems) {
      system.preTick(delta)
    }
  }

  preFixedUpdate(willFixedStep) {
    for (const item of this.hot) {
      item.preFixedUpdate?.(willFixedStep)
    }
    for (const system of this.systems) {
      system.preFixedUpdate(willFixedStep)
    }
  }

  fixedUpdate(delta) {
    for (const item of this.hot) {
      item.fixedUpdate?.(delta)
    }
    for (const system of this.systems) {
      system.fixedUpdate(delta)
    }
  }

  postFixedUpdate(delta) {
    for (const item of this.hot) {
      item.postFixedUpdate?.(delta)
    }
    for (const system of this.systems) {
      system.postFixedUpdate(delta)
    }
  }

  preUpdate(alpha) {
    for (const item of this.hot) {
      item.preUpdate?.(alpha)
    }
    for (const system of this.systems) {
      system.preUpdate(alpha)
    }
  }

  update(delta) {
    for (const item of this.hot) {
      item.update?.(delta)
    }
    for (const system of this.systems) {
      system.update(delta)
    }
  }

  postUpdate(delta) {
    for (const item of this.hot) {
      item.postUpdate?.(delta)
    }
    for (const system of this.systems) {
      system.postUpdate(delta)
    }
  }

  lateUpdate(delta) {
    for (const item of this.hot) {
      item.lateUpdate?.(delta)
    }
    for (const system of this.systems) {
      system.lateUpdate(delta)
    }
  }

  postLateUpdate(delta) {
    for (const item of this.hot) {
      item.postLateUpdate?.(delta)
    }
    for (const system of this.systems) {
      system.postLateUpdate(delta)
    }
  }

  commit(delta) {
    for (const item of this.hot) {
      item.commit?.(delta)
    }
    for (const system of this.systems) {
      system.commit(delta)
    }
  }

  postTick() {
    for (const system of this.systems) {
      system.postTick()
    }
  }

  setHot(item, hot) {
    if (hot) {
      this.hot.add(item)
    } else {
      this.hot.delete(item)
    }
  }

  resolveURL(url) {
    if (!url) return url
    url = url.trim()
    if (url.startsWith('blob')) {
      return url
    }
    if (url.startsWith('asset://')) {
      return url.replace('asset:/', this.assetsUrl) // TODO
    }
    if (url.match(/^https?:\/\//i)) {
      return url
    }
    if (url.startsWith('//')) {
      return `https:${url}`
    }
    if (url.startsWith('/')) {
      return url
    }
    return `https://${url}`
  }
}
